import React from 'react';
import { Box } from '@mui/material';

/**
 * Small circular badge showing a DICOM modality abbreviation (XR/US/MR/CT/...).
 *
 * Replaces abstract MUI icons (InvertColors, Waves, Scanner, DonutLarge) that
 * don't telegraph "imaging" to clinicians. Two-letter modality codes are the
 * convention every PACS / RIS uses, so this is what the eye recognizes.
 *
 * Color choices follow common PACS conventions (gray X-ray, purple US,
 * blue MR, orange CT, etc.) so the timeline stays scannable at a glance.
 */
const PALETTE: Record<string, { label: string; bg: string }> = {
  // X-ray family
  DX: { label: 'XR', bg: '#5b6b7a' },
  DR: { label: 'XR', bg: '#5b6b7a' },
  XR: { label: 'XR', bg: '#5b6b7a' },
  CR: { label: 'XR', bg: '#5b6b7a' },
  'X-RAY': { label: 'XR', bg: '#5b6b7a' },
  RADIOGRAPH: { label: 'XR', bg: '#5b6b7a' },
  // Ultrasound
  US: { label: 'US', bg: '#7c4dff' },
  ULTRASOUND: { label: 'US', bg: '#7c4dff' },
  // Magnetic resonance
  MR: { label: 'MR', bg: '#1976d2' },
  MRI: { label: 'MR', bg: '#1976d2' },
  // Computed tomography
  CT: { label: 'CT', bg: '#ed6c02' },
  // Endoscopy
  ES: { label: 'ES', bg: '#2e7d32' },
  ENDOSCOPY: { label: 'ES', bg: '#2e7d32' },
  // Nuclear / scintigraphy
  NM: { label: 'NM', bg: '#9c27b0' },
  SCINTIGRAPHY: { label: 'NM', bg: '#9c27b0' },
  // Cardiac
  ECG: { label: 'ECG', bg: '#d32f2f' },
  EKG: { label: 'ECG', bg: '#d32f2f' },
  ELECTROCARDIOGRAM: { label: 'ECG', bg: '#d32f2f' },
};

interface Props {
  /** Raw modality string from Keystone or human-entered (e.g. "DX", "Ultrasound", "MRI"). */
  name?: string | null;
  /** Diameter in pixels. Default 32. */
  size?: number;
}

const ModalityBadge: React.FC<Props> = ({ name, size = 32 }) => {
  const upper = (name ?? '').toUpperCase().trim();
  const entry =
    PALETTE[upper] ??
    // Fall back: take first 2 chars uppercase, neutral gray.
    { label: upper.slice(0, 2) || '??', bg: '#6b7280' };
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: entry.bg,
        color: 'white',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size <= 28 ? 10 : 11,
        fontWeight: 700,
        letterSpacing: 0.5,
        flexShrink: 0,
      }}
      aria-label={`Modality: ${name ?? 'unknown'}`}
    >
      {entry.label}
    </Box>
  );
};

export default ModalityBadge;
